import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCamera, 
  faXmark, 
  faCircleInfo, 
  faCheck, 
  faSliders, 
  faBolt, 
  faWandMagicSparkles
} from '@fortawesome/free-solid-svg-icons';

const CameraCheckModal = ({ onClose, onPhotoTaken, processingImage = false }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  
  // 启动摄像头
  useEffect(() => {
    const startCamera = async () => {
      try {
        // 增加更多的相机约束选项，提高兼容性
        const constraints = { 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false 
        };
        
        // 针对iOS设备的特殊处理
        if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
          console.log('检测到iOS设备，使用特殊相机配置');
          // 在iOS上尝试不同的配置
          try {
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            handleStreamSuccess(mediaStream);
          } catch (iosError) {
            console.error('iOS首次尝试失败:', iosError);
            // 尝试简化配置
            const simpleConstraints = { video: true, audio: false };
            try {
              const mediaStream = await navigator.mediaDevices.getUserMedia(simpleConstraints);
              handleStreamSuccess(mediaStream);
            } catch (simpleError) {
              handleCameraError(simpleError);
            }
          }
        } else {
          // 非iOS设备使用标准方法
          const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
          handleStreamSuccess(mediaStream);
        }
      } catch (err) {
        handleCameraError(err);
      }
    };
    
    // 处理流媒体成功
    const handleStreamSuccess = (mediaStream) => {
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => console.error('视频播放失败:', e));
        };
        setStream(mediaStream);
        setCameraError(null);
      }
    };
    
    // 处理相机错误
    const handleCameraError = (err) => {
      console.error('摄像头访问错误:', err);
      let errorMessage = '无法访问摄像头，请确保允许浏览器使用摄像头权限。';
      
      // 针对iOS提供更具体的错误信息
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        errorMessage += '\n\n在iOS设备上，请尝试:\n1. 确保在"设置 > Safari > 相机"中允许访问\n2. 使用Safari浏览器\n3. 确保网站使用HTTPS协议';
      }
      
      setCameraError(errorMessage);
    };
    
    startCamera();
    
    // 组件卸载时停止摄像头
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);
  
  // 拍照
  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // 设置canvas尺寸与视频一致
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // 在canvas上绘制视频帧
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // 获取图片数据
    const imageData = canvas.toDataURL('image/jpeg');
    setCapturedImage(imageData);
  };
  
  // 重新拍照
  const retakePhoto = () => {
    setCapturedImage(null);
  };
  
  // 确认使用照片
  const confirmPhoto = () => {
    if (capturedImage && onPhotoTaken) {
      onPhotoTaken(capturedImage);
    }
  };
  
  // 关闭模态框
  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex flex-col">
      {/* 顶部导航 */}
      <div className="p-4 flex justify-between items-center">
        <button 
          onClick={handleClose}
          className="text-white text-xl"
          disabled={processingImage}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <h2 className="text-white text-lg font-bold">拍照检查</h2>
        <div className="w-8"></div>
      </div>
      
      {/* 主要内容 */}
      <div className="flex-1 flex flex-col p-4">
        {/* 相机说明 */}
        {!capturedImage && (
          <div className="bg-blue-900 bg-opacity-50 rounded-xl p-4 mb-4">
            <h4 className="font-medium text-blue-200 mb-2 flex items-center">
              <FontAwesomeIcon icon={faCircleInfo} className="mr-2" />
              使用说明
            </h4>
            <ul className="text-sm text-blue-100 space-y-2">
              <li className="flex items-start">
                <FontAwesomeIcon icon={faCheck} className="mr-2 mt-1 text-blue-300 text-xs" />
                <span>将您写下的单词或短文放在框内</span>
              </li>
              <li className="flex items-start">
                <FontAwesomeIcon icon={faCheck} className="mr-2 mt-1 text-blue-300 text-xs" />
                <span>确保光线充足，字迹清晰</span>
              </li>
              <li className="flex items-start">
                <FontAwesomeIcon icon={faCheck} className="mr-2 mt-1 text-blue-300 text-xs" />
                <span>点击按钮拍照，系统将自动识别您的答案</span>
              </li>
            </ul>
          </div>
        )}
        
        {/* 相机视图或预览图 */}
        <div className="relative flex-1 bg-black rounded-xl overflow-hidden mb-4">
          {cameraError ? (
            <div className="absolute inset-0 flex items-center justify-center text-white text-center p-4">
              <div>
                <div className="text-red-400 text-4xl mb-2">
                  <FontAwesomeIcon icon={faCamera} />
                </div>
                <p>{cameraError}</p>
              </div>
            </div>
          ) : capturedImage ? (
            <img 
              src={capturedImage} 
              alt="拍摄的照片" 
              className="w-full h-full object-contain"
            />
          ) : (
            <>
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                className="w-full h-full object-cover"
              />
              <div className="absolute top-0 left-0 right-0 bottom-0 border-2 border-dashed border-white/70 m-8 rounded-lg pointer-events-none"></div>
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 opacity-70 shadow-lg animate-[scan_2s_infinite_linear]"></div>
              <div className="absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-between p-4">
                <div className="flex justify-between">
                  <button className="bg-white/20 rounded-full p-2 text-white">
                    <FontAwesomeIcon icon={faBolt} />
                  </button>
                  <button className="bg-white/20 rounded-full p-2 text-white">
                    <FontAwesomeIcon icon={faSliders} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* 隐藏的canvas用于处理图像 */}
        <canvas ref={canvasRef} className="hidden"></canvas>
        
        {/* 底部按钮 */}
        {processingImage ? (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium">识别状态</h4>
              <span className="text-xs text-blue-500 animate-pulse">AI处理中...</span>
            </div>
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <FontAwesomeIcon icon={faWandMagicSparkles} className="text-blue-500 animate-spin" />
              </div>
              <div className="flex-1">
                <div className="h-2 bg-blue-200 rounded-full mb-2 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-blue-500 w-1/2 animate-[pulse_1s_ease-in-out_infinite]"></div>
                </div>
                <div className="h-2 bg-blue-200 rounded-full w-2/3 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-blue-500 w-1/3 animate-[pulse_1s_ease-in-out_infinite]"></div>
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              <FontAwesomeIcon icon={faCircleInfo} className="mr-1" />
              AI正在分析您的答案，请稍候...
            </div>
          </div>
        ) : capturedImage ? (
          <div className="grid grid-cols-2 gap-4">
            <button 
              onClick={retakePhoto}
              className="bg-white text-blue-600 border border-blue-200 py-3 rounded-xl font-bold shadow-sm"
            >
              重新拍照
            </button>
            <button 
              onClick={confirmPhoto}
              className="bg-blue-500 text-white py-3 rounded-xl font-bold shadow-sm"
            >
              确认使用
            </button>
          </div>
        ) : (
          <button 
            onClick={takePhoto}
            className="bg-white rounded-full w-16 h-16 mx-auto flex items-center justify-center border-4 border-blue-500 animate-[pulse_1.5s_infinite]"
          >
          </button>
        )}
      </div>
    </div>
  );
};

export default CameraCheckModal;